import { useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './admin/AdminAuthContext';
import { AdvertiserAuthProvider, useAdvertiserAuth } from './advertiser/AdvertiserAuthContext';
import { getPortal, PORTALS } from './lib/portal';
import Navbar from './components/Navbar';
import AnalyticsTracker from './components/AnalyticsTracker';
import CookieConsentBanner from './components/CookieConsentBanner';
import Footer from './components/Footer';
import './App.css';

// ── Eager-loaded (initial paint / most-hit routes) ──────────────────────────
// AuthPage is the unauthenticated landing view (LCP-critical).
// FeedPage is the default post-login destination and effectively the app home.
import AuthPage from './pages/AuthPage';
import FeedPage from './pages/FeedPage';

// ── Lazy-loaded consumer pages (route-level code splitting) ─────────────────
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ItemsPage = lazy(() => import('./pages/ItemsPage'));
const ItemFormPage = lazy(() => import('./pages/ItemFormPage'));
const ItemDetailPage = lazy(() => import('./pages/ItemDetailPage'));
const GiveawaysPage = lazy(() => import('./pages/GiveawaysPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const CommunitiesPage = lazy(() => import('./pages/CommunitiesPage'));
const CommunityDetailPage = lazy(() => import('./pages/CommunityDetailPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const BorrowRequestsPage = lazy(() => import('./pages/BorrowRequestsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EarlyAccessPage = lazy(() => import('./pages/EarlyAccessPage'));
// LandingPage is shared — used as the full landing portal (www.*) and
// as `/` and `/landing` inside every environment.
const LandingPage = lazy(() => import('./pages/LandingPage'));

// ── Lazy-loaded admin bundle (only reached from /admin subdomain or path) ───
const AdminLoginPage = lazy(() => import('./admin/AdminLoginPage'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const DashboardPage = lazy(() => import('./admin/pages/DashboardPage'));
const AdminUsersPage = lazy(() => import('./admin/pages/UsersPage'));
const AdminCommunitiesPage = lazy(() => import('./admin/pages/CommunitiesPage'));
const AdminItemsPage = lazy(() => import('./admin/pages/ItemsPage'));
const AdminProjectsPage = lazy(() => import('./admin/pages/ProjectsPage'));
const AdminReportsPage = lazy(() => import('./admin/pages/ReportsPage'));
const AdminAdvertisersPage = lazy(() => import('./admin/pages/AdvertisersPage'));
const AdminDisputesPage = lazy(() => import('./admin/pages/DisputesPage'));
const AdminToolRequestsPage = lazy(() => import('./admin/pages/ToolRequestsPage'));
const AdminBetaPage = lazy(() => import('./admin/pages/AdminBetaPage'));
const PromoCodesPage = lazy(() => import('./admin/pages/PromoCodesPage'));
const AdminSecurityReportsPage = lazy(() => import('./admin/pages/SecurityReportsPage'));
const AdminAccountSecurityPage = lazy(() => import('./admin/pages/AccountSecurityPage'));
const AdminDevToolsPage = lazy(() => import('./admin/pages/DevToolsPage'));
const AdminTeamPage = lazy(() => import('./admin/pages/AdminTeamPage'));
const AuditLogPage = lazy(() => import('./admin/pages/AuditLogPage'));
const RequirePermission = lazy(() => import('./admin/components/RequirePermission'));

// ── Lazy-loaded advertiser bundle (only reached from /advertiser subdomain) ─
const AdvertiserLoginPage = lazy(() => import('./advertiser/AdvertiserLoginPage'));
const AdvertiserResetPasswordPage = lazy(() => import('./advertiser/AdvertiserResetPasswordPage'));
const AdvertiserLayout = lazy(() => import('./advertiser/AdvertiserLayout'));
const AdvertiserDashboardPage = lazy(() => import('./advertiser/pages/DashboardPage'));
const AdvertiserServicesPage = lazy(() => import('./advertiser/pages/ServicesPage'));
const AdvertiserServiceFormPage = lazy(() => import('./advertiser/pages/ServiceFormPage'));
const AdvertiserSettingsPage = lazy(() => import('./advertiser/pages/SettingsPage'));
const AdvertiserInquiriesPage = lazy(() => import('./advertiser/pages/InquiriesPage'));
const AdvertiserSubscriptionPage = lazy(() => import('./advertiser/pages/SubscriptionPage'));
const AdvertiserResearchPage = lazy(() => import('./advertiser/pages/ResearchPage'));

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-transparent">
    <div className="w-10 h-10 border-4 border-[#004B00] border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * In subdomain mode a legacy `<Link to="/admin/dashboard">` would 404
 * because the routes are mounted at `/*`, not `/admin/*`. This tiny
 * component catches the legacy prefix and redirects to the same path
 * without it — so sidebar/link code that was written for the
 * path-prefix deployment keeps working when the same bundle is served
 * from `admin.<domain>` or `advertiser.<domain>`.
 */
const LegacyPrefixRedirect = ({ strip }) => {
  const location = useLocation();
  const clean = location.pathname.startsWith(strip)
    ? location.pathname.slice(strip.length) || '/'
    : location.pathname;
  return <Navigate to={`${clean}${location.search}${location.hash}`} replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      <Navbar />
      <div className="pb-16 md:pb-0 min-h-screen bg-transparent flex flex-col">
        <div className="flex-1">{children}</div>
        <Footer />
      </div>
    </>
  );
};

const AdminRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  if (loading) return <Spinner />;
  if (!admin) return <Navigate to="/admin-login" replace />;
  return children;
};

const AdvertiserRoute = ({ children }) => {
  const { advertiser, loading } = useAdvertiserAuth();
  if (loading) return <Spinner />;
  if (!advertiser) return <Navigate to="/advertiser-portal" replace />;
  return children;
};

const AdminPanel = () => {
  const [pendingReports, setPendingReports] = useState(0);
  return (
    <AdminRoute>
      <AdminLayout pendingReports={pendingReports}>
        <Routes>
          <Route path="dashboard" element={<RequirePermission perm="dashboard.view"><DashboardPage onStatsLoaded={setPendingReports} /></RequirePermission>} />
          <Route path="users" element={<RequirePermission perm="users.view"><AdminUsersPage /></RequirePermission>} />
          <Route path="communities" element={<RequirePermission perm="communities.view"><AdminCommunitiesPage /></RequirePermission>} />
          <Route path="items" element={<RequirePermission perm="items.view"><AdminItemsPage /></RequirePermission>} />
          <Route path="projects" element={<RequirePermission perm="projects.view"><AdminProjectsPage /></RequirePermission>} />
          <Route path="advertisers" element={<RequirePermission perm="advertisers.view"><AdminAdvertisersPage /></RequirePermission>} />
          <Route path="reports" element={<RequirePermission perm="reports.view"><AdminReportsPage /></RequirePermission>} />
          <Route path="disputes" element={<RequirePermission perm="disputes.view"><AdminDisputesPage /></RequirePermission>} />
          <Route path="tool-requests" element={<RequirePermission perm="tool_requests.view"><AdminToolRequestsPage /></RequirePermission>} />
          <Route path="beta" element={<RequirePermission perm="beta.view"><AdminBetaPage /></RequirePermission>} />
          <Route path="promo-codes" element={<RequirePermission perm="promos.view"><PromoCodesPage /></RequirePermission>} />
          <Route path="security-reports" element={<RequirePermission perm="security.view"><AdminSecurityReportsPage /></RequirePermission>} />
          <Route path="account-security" element={<AdminAccountSecurityPage />} />
          <Route path="dev-tools" element={<RequirePermission perm="devtools.use"><AdminDevToolsPage /></RequirePermission>} />
          <Route path="team" element={<RequirePermission perm="admins.manage"><AdminTeamPage /></RequirePermission>} />
          <Route path="audit-log" element={<RequirePermission perm="audit.view"><AuditLogPage /></RequirePermission>} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Routes>
      </AdminLayout>
    </AdminRoute>
  );
};

const AdvertiserPanel = () => {
  return (
    <AdvertiserRoute>
      <AdvertiserLayout>
        <Routes>
          <Route path="dashboard" element={<AdvertiserDashboardPage />} />
          <Route path="services" element={<AdvertiserServicesPage />} />
          <Route path="services/new" element={<AdvertiserServiceFormPage />} />
          <Route path="services/:serviceId" element={<AdvertiserServiceFormPage />} />
          <Route path="services/:serviceId/edit" element={<AdvertiserServiceFormPage />} />
          <Route path="subscription" element={<AdvertiserSubscriptionPage />} />
          <Route path="research" element={<AdvertiserResearchPage />} />
          <Route path="settings" element={<AdvertiserSettingsPage />} />
          <Route path="inquiries" element={<AdvertiserInquiriesPage />} />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Routes>
      </AdvertiserLayout>
    </AdvertiserRoute>
  );
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/feed" replace /> : <AuthPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/items" element={<ProtectedRoute><ItemsPage scope="lend" /></ProtectedRoute>} />
      <Route path="/my-giveaways" element={<ProtectedRoute><ItemsPage scope="giveaway" /></ProtectedRoute>} />
      <Route path="/items/new" element={<ProtectedRoute><ItemFormPage /></ProtectedRoute>} />
      <Route path="/items/:itemId/edit" element={<ProtectedRoute><ItemFormPage /></ProtectedRoute>} />
      <Route path="/items/:itemId" element={<ProtectedRoute><ItemDetailPage /></ProtectedRoute>} />
      <Route path="/giveaways" element={<ProtectedRoute><GiveawaysPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
      <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
      <Route path="/communities" element={<ProtectedRoute><CommunitiesPage /></ProtectedRoute>} />
      <Route path="/communities/:communityId" element={<ProtectedRoute><CommunityDetailPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/messages/:userId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/borrow-requests" element={<ProtectedRoute><BorrowRequestsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/early-access" element={<EarlyAccessPage />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Detect which portal this tab is running in. In subdomain mode we only
  // mount ONE portal's route tree — so `admin.neighborli.com/settings`
  // resolves to the admin settings page, not the consumer one.
  const portal = getPortal();

  // Landing portal — www.neighborli.net (public marketing site)
  if (portal === PORTALS.LANDING) {
    return (
      <BrowserRouter>
        <AnalyticsTracker />
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/early-access" element={<EarlyAccessPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
        <CookieConsentBanner />
      </BrowserRouter>
    );
  }

  if (portal === PORTALS.ADMIN) {
    return (
      <BrowserRouter>
        <AnalyticsTracker />
        <AdminAuthProvider>
          <Suspense fallback={<Spinner />}>
            <Routes>
              {/* Root of the admin subdomain routes to the login page (which
                  itself redirects to /dashboard when already authenticated). */}
              <Route path="/" element={<AdminLoginWrapper />} />
              <Route path="/login" element={<AdminLoginWrapper />} />
              <Route path="/admin-login" element={<AdminLoginWrapper />} />
              {/* Legacy path-prefix links (e.g. hardcoded `<Link to="/admin/x">`
                  from the sidebar) transparently strip the `/admin` prefix so
                  the shared components keep working across both deploy modes. */}
              <Route path="/admin/*" element={<LegacyPrefixRedirect strip="/admin" />} />
              <Route path="/*" element={<AdminPanel />} />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    );
  }

  if (portal === PORTALS.ADVERTISER) {
    return (
      <BrowserRouter>
        <AnalyticsTracker />
        <AdvertiserAuthProvider>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/" element={<AdvertiserLoginWrapper />} />
              <Route path="/login" element={<AdvertiserLoginWrapper />} />
              <Route path="/reset-password" element={<AdvertiserResetPasswordPage />} />
              {/* Legacy path-prefix links transparently stripped. */}
              <Route path="/advertiser-portal/*" element={<LegacyPrefixRedirect strip="/advertiser-portal" />} />
              <Route path="/*" element={<AdvertiserPanel />} />
            </Routes>
          </Suspense>
        </AdvertiserAuthProvider>
        <Toaster position="top-right" richColors />
        <CookieConsentBanner />
      </BrowserRouter>
    );
  }

  // Consumer portal (default) — path-prefix mode preserves all three
  // portals under one origin for the existing single-domain deploy.
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <AdminAuthProvider>
        <AdvertiserAuthProvider>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/admin-login" element={<AdminLoginWrapper />} />
              <Route path="/admin/*" element={<AdminPanel />} />
              <Route path="/advertiser-portal" element={<AdvertiserLoginWrapper />} />
              <Route path="/advertiser-portal/reset-password" element={<AdvertiserResetPasswordPage />} />
              <Route path="/advertiser-portal/*" element={<AdvertiserPanel />} />
              <Route path="/*" element={
                <AuthProvider>
                  <AppRoutes />
                  <Toaster position="top-right" richColors />
                </AuthProvider>
              } />
            </Routes>
          </Suspense>
        </AdvertiserAuthProvider>
      </AdminAuthProvider>
      <Toaster position="top-right" richColors />
      <CookieConsentBanner />
    </BrowserRouter>
  );
}

const AdminLoginWrapper = () => {
  const { admin } = useAdminAuth();
  if (admin) return <Navigate to="/admin/dashboard" replace />;
  return <AdminLoginPage />;
};

const AdvertiserLoginWrapper = () => {
  const { advertiser } = useAdvertiserAuth();
  if (advertiser) return <Navigate to="/advertiser-portal/dashboard" replace />;
  return <AdvertiserLoginPage />;
};

export default App;

